import { motion, useMotionValue, useTransform, PanInfo, useAnimation } from "framer-motion";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Star, Zap, GraduationCap, BookOpen, MapPin, TrendingUp } from "lucide-react";
import { UserProfile } from "@/hooks/useUsers";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Progress } from "@/components/ui/progress";

interface SwipeCardProps {
  user: UserProfile;
  onSwipe: (direction: "left" | "right") => void;
  isTop?: boolean;
  stackIndex?: number;
  matchPercentage?: number;
}

const SwipeCard = ({ user, onSwipe, isTop = false, stackIndex = 0, matchPercentage = 0 }: SwipeCardProps) => {
  const x = useMotionValue(0);
  const controls = useAnimation();
  
  // Rotation based on drag position
  const rotate = useTransform(x, [-300, 0, 300], [-25, 0, 25]);
  
  // Opacity of like/nope indicators
  const likeOpacity = useTransform(x, [0, 100], [0, 1]);
  const nopeOpacity = useTransform(x, [-100, 0], [1, 0]);
  
  // Scale for stacked cards effect
  const scale = 1 - stackIndex * 0.05;
  const yOffset = stackIndex * 8;

  const handleDragEnd = async (_: MouseEvent | TouchEvent | PointerEvent, info: PanInfo) => {
    const threshold = 100;
    if (info.offset.x > threshold) {
      await controls.start({ x: 500, opacity: 0, transition: { duration: 0.3 } });
      onSwipe("right");
    } else if (info.offset.x < -threshold) {
      await controls.start({ x: -500, opacity: 0, transition: { duration: 0.3 } });
      onSwipe("left");
    } else {
      controls.start({ x: 0, transition: { type: "spring", stiffness: 300, damping: 25 } });
    }
  };

  // Get match percentage color
  const getMatchColor = () => {
    if (matchPercentage >= 80) return "text-green-500";
    if (matchPercentage >= 50) return "text-yellow-500";
    return "text-orange-500";
  };

  return (
    <motion.div
      className="absolute inset-0 cursor-grab active:cursor-grabbing"
      style={{
        x: isTop ? x : 0,
        rotate: isTop ? rotate : 0,
        zIndex: 10 - stackIndex,
      }}
      animate={isTop ? controls : { scale, opacity: 1, y: yOffset }}
      initial={{ scale: 0.9, opacity: 0, y: yOffset }}
      drag={isTop ? "x" : false}
      dragConstraints={{ left: 0, right: 0 }}
      dragElastic={0.9}
      onDragEnd={isTop ? handleDragEnd : undefined}
      transition={{ type: "spring", stiffness: 300, damping: 25 }}
    >
      <Card className="h-full w-full overflow-hidden border-2 shadow-strong bg-card">
        {/* Like/Nope Indicators */}
        {isTop && (
          <>
            <motion.div
              className="absolute top-6 left-6 z-20 rotate-[-20deg]"
              style={{ opacity: nopeOpacity }}
            >
              <div className="border-4 border-destructive text-destructive font-bold text-3xl px-4 py-2 rounded-lg bg-card/90">
                NOPE
              </div>
            </motion.div>
            <motion.div
              className="absolute top-6 right-6 z-20 rotate-[20deg]"
              style={{ opacity: likeOpacity }}
            >
              <div className="border-4 border-green-500 text-green-500 font-bold text-3xl px-4 py-2 rounded-lg bg-card/90">
                LIKE
              </div>
            </motion.div>
          </>
        )}

        {/* User Image Section */}
        <div className="relative h-[45%] gradient-primary overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-b from-transparent to-black/50" />
          <Avatar className="w-full h-full rounded-none">
            <AvatarImage 
              src={user.avatar_url || `https://api.dicebear.com/7.x/avataaars/svg?seed=${user.id}`} 
              alt={user.name}
              className="w-full h-full object-cover"
            />
            <AvatarFallback className="w-full h-full rounded-none text-6xl bg-primary">
              {user.name.charAt(0).toUpperCase()}
            </AvatarFallback>
          </Avatar>
          
          {/* Match Percentage Badge */}
          {matchPercentage > 0 && (
            <div className="absolute top-4 left-4 bg-card/95 backdrop-blur-sm rounded-lg px-3 py-2 shadow-lg">
              <div className="flex items-center gap-2">
                <TrendingUp className={`h-4 w-4 ${getMatchColor()}`} />
                <span className={`font-bold text-lg ${getMatchColor()}`}>{matchPercentage}%</span>
              </div>
              <p className="text-xs text-muted-foreground">Match</p>
            </div>
          )}
          
          {/* Availability Badge */}
          {user.is_available && (
            <Badge className="absolute top-4 right-4 bg-green-500 text-white border-0 shadow-lg">
              <Zap className="h-3 w-3 mr-1" />
              Available Now
            </Badge>
          )}
          
          {/* Name and Info Overlay */}
          <div className="absolute bottom-0 left-0 right-0 p-4 text-white">
            <div className="flex items-end justify-between">
              <div>
                <h3 className="font-bold text-2xl">{user.name}</h3>
                <div className="flex items-center gap-2 text-white/90 text-sm">
                  <Star className="h-4 w-4 fill-yellow-400 text-yellow-400" />
                  <span>{(user.rating || 0).toFixed(1)}</span>
                  <span className="mx-1">•</span>
                  <span>{user.sessions_completed || 0} sessions</span>
                </div>
              </div>
              {user.location && (
                <div className="flex items-center gap-1 text-white/80 text-sm">
                  <MapPin className="h-4 w-4" />
                  <span>{user.location}</span>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Content Section */}
        <div className="h-[55%] p-5 space-y-4 overflow-y-auto">
          {/* Bio */}
          {user.bio && (
            <p className="text-muted-foreground text-sm line-clamp-2">{user.bio}</p>
          )}

          {/* Teaching Skills */}
          <div>
            <h4 className="text-sm font-semibold text-muted-foreground mb-2 flex items-center gap-2">
              <GraduationCap className="h-4 w-4 text-primary" />
              Can Teach
            </h4>
            {user.teaching_skills.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {user.teaching_skills.slice(0, 5).map((skill) => (
                  <Badge 
                    key={skill.id}
                    variant="default"
                    className="text-sm"
                  >
                    {skill.emoji} {skill.name}
                  </Badge>
                ))}
                {user.teaching_skills.length > 5 && (
                  <Badge variant="secondary">+{user.teaching_skills.length - 5}</Badge>
                )}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">No skills yet</p>
            )}
          </div>

          {/* Learning Skills */}
          <div>
            <h4 className="text-sm font-semibold text-muted-foreground mb-2 flex items-center gap-2">
              <BookOpen className="h-4 w-4 text-secondary-foreground" />
              Wants to Learn
            </h4>
            {user.learning_skills.length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {user.learning_skills.slice(0, 5).map((skill) => (
                  <Badge 
                    key={skill.id}
                    variant="outline"
                    className="text-sm"
                  >
                    {skill.emoji} {skill.name}
                  </Badge>
                ))}
                {user.learning_skills.length > 5 && (
                  <Badge variant="secondary">+{user.learning_skills.length - 5}</Badge>
                )}
              </div>
            ) : (
              <p className="text-sm text-muted-foreground">No goals yet</p>
            )}
          </div>

          {/* Stats */}
          <div className="flex items-center gap-4 pt-2 border-t border-border">
            <div className="flex items-center gap-2 text-sm">
              <div className="p-1.5 rounded-lg bg-primary/10">
                <Star className="h-4 w-4 text-primary" />
              </div>
              <div>
                <span className="font-semibold">{user.renaissance_score || 0}</span>
                <span className="text-muted-foreground text-xs ml-1">Score</span>
              </div>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <div className="p-1.5 rounded-lg bg-secondary/50">
                <Zap className="h-4 w-4 text-secondary-foreground" />
              </div>
              <div>
                <span className="font-semibold">{user.level || "Beginner"}</span>
                <span className="text-muted-foreground text-xs ml-1">Level</span>
              </div>
            </div>
          </div>
        </div>
      </Card>
    </motion.div>
  );
};

export default SwipeCard;
